import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Platform } from 'react-native';

import HomePage from './HomePage.component';
import {
	getUserData,
	getEmployeePrivileges,
} from '../../store/selectors/login.selectors';
import httpService from '../../services/http.service';
import { Employee101Data } from '../ReportPDFPage/ReportPDFPage.utils';
import {
	Home_Both_Permissions,
	Home_One_Permissions,
	Home_Paycheck_Permissions,
	Privileges,
	Role,
} from '../../constants/roleTypes.constant';
import { getState } from '../../store/store';
import moment from 'moment';
import { InAppMessages } from '../../components/shared/InAppMessages/InAppMessages';
import { useMonthAttendance } from '../../services/hooks/useMonthAttendance.hook';

type EmployeeDashboardResponse = {
	yearMonth: string;
	total: string;
	deductions: string;
	otherDeductions: string;
	paymentNet: string;
	vacationBalance: string;
	employeeFormId: string;
	employeeFormStatus: boolean | null;
};

export type HomePageReportContentKeyType =
	| 'total'
	| 'deductions'
	| 'otherDeductions'
	| 'paymentNet'
	| 'vacationBalance';

export type HomePageReportContentElType = Record<
	HomePageReportContentKeyType,
	string
>;

export type HomePageMonthlyHoursDataResponse = {
	year: number;
	month: number;
	total: number;
};

export type HomePageWeeklyHoursDataResponse = {
	date: string;
	workLogs: number | null;
};

const initContent = {
	total: '',
	deductions: '',
	otherDeductions: '',
	paymentNet: '',
	vacationBalance: '',
};

const HomePageContainer = ({ navigation }: { navigation: any }) => {
	const userData = useSelector(getUserData);
	const [showProjectList, setShowProjectList] = useState(true);
	const [isSummaryOnly, setIsSummaryOnly] = useState<boolean | null>(null);
	const [isPaycheckBlockShown, setIsPaycheckBlockShown] = useState(false);
	const [form101Id, setForm101Id] = useState<string | null>(null);
	const [form101Status, setForm101Status] = useState<boolean | null>(null);
	const [form101StatusCode, setForm101StatusCode] = useState<
		Employee101Data['statusCode'] | null
	>(null);
	const [form101LastUpdate, setForm101LastUpdate] = useState<Date | null>(
		null,
	);

	const [reportDateYear, setDateYear] = useState<string>('');
	const [reportContentData, setReportContentData] =
		useState<HomePageReportContentElType>(initContent);
	const [report101warning, setReport101Warning] = useState<boolean>(false);
	const [monthlyHoursData, setMonthlyHoursData] = useState<
		HomePageMonthlyHoursDataResponse[] | null
	>(null);
	const [weeklyData, setWeeklyData] = useState<
		HomePageWeeklyHoursDataResponse[] | null
	>(null);

	const employeePrivilegesStatus = useSelector(getEmployeePrivileges);

	const CURRENT_MONTH = moment().month() + 1;
	const CURRENT_YEAR = moment().year();

	useEffect(() => {
		fetchEmployeeDashboard();
		onMount();
	}, [userData, employeePrivilegesStatus]);

	useEffect(() => {
		if (isSummaryOnly === false) {
			getMonthlyHoursData();
			getWeeklyHoursData();
		}
	}, [isSummaryOnly]);

	// useEffect(() => {
	// 	onWorkerCampaign();
	// }, []);

	// const onWorkerCampaign = async () => {
	// 	await modalService.openModal(null, {}, (props: any) => (
	// 		<UserCampaignModal />
	// 	));
	// };

	const onMount = async () => {
		if (Platform.OS !== 'web') {
			const crashlytics = (
				await import('@react-native-firebase/crashlytics')
			).default;
			crashlytics().log('HomePage');
			await crashlytics().setAttributes({
				userId: userData?.id || '',
				userName:
					`${userData?.first_name} ${userData?.last_name}` || '',
			});
		}
	};

	const fetchEmployeeDashboard = async () => {
		try {
			const res = await httpService
				.api<any>({
					type: 'getEmployeeDashboard',
				})
				.then(async (res: EmployeeDashboardResponse) => {
					let employeeFormStatus =
						res.employeeFormStatus === null ? false : true;
					accessMatcher(employeeFormStatus);

					const is101FinishWorkDateExpired =
						moment(
							getState().dateTime.currentDateTime,
							'YYYY-MM-DD',
						).year() >
						moment(
							userData?.operative_module?.[113]?.finish_work,
							'YYYY-MM-DD',
						).year();
					setForm101Id(
						is101FinishWorkDateExpired ? null : res?.employeeFormId,
					);
					setForm101Status(
						is101FinishWorkDateExpired
							? null
							: res.employeeFormStatus,
					);

					if (!is101FinishWorkDateExpired && res?.employeeFormId) {
						try {
							const forms: Employee101Data[] =
								await httpService.api({
									type: 'getOneHandredOneReportRange',
								});
							if (forms?.length > 0) {
								const match =
									forms.find(
										f =>
											String(f.id) === res.employeeFormId,
									) ?? forms[0];
								setForm101StatusCode(match.statusCode);
								setForm101LastUpdate(match.lastUpdate ?? null);
							}
						} catch (e) {
							console.log(
								'Error fetching 101 form status code',
								e,
							);
						}
					} else {
						setForm101StatusCode(null);
						setForm101LastUpdate(null);
					}

					const employeeRolesPrivileges =
						getState().login?.user?.employeeRolePrivileges;

					if (employeeFormStatus) {
						setReport101Warning(
							!res.employeeFormStatus &&
								!is101FinishWorkDateExpired &&
								!!employeeRolesPrivileges?.includes(
									Privileges.ROLE_EMPLOYEE_FORM_WRITE,
								),
						);
					}
					setDateYear(res.yearMonth);
					setReportContentData({
						deductions: res.deductions,
						otherDeductions: res.otherDeductions,
						paymentNet: res.paymentNet,
						total: res.total,
						vacationBalance: res.vacationBalance,
					});
				});
		} catch (e) {
			console.log(
				`Error while fetching data from API: getEmployeeDashboard`,
				e,
			);
		}
	};

	const getMonthlyHoursData = async () => {
		try {
			const res = await httpService.api<
				HomePageMonthlyHoursDataResponse[]
			>({
				type: 'getMonthlyHoursData',
				params: {
					fromYear: moment().subtract(5, 'months').year(),
					fromMonth: moment().subtract(5, 'months').month() + 1,
					toYear: moment().year(),
					toMonth: moment().month() + 1,
				},
			});
			setMonthlyHoursData(res);
		} catch (e) {
			console.log(
				`Error while fetching data from API: getMonthlyHoursData`,
				e,
			);
		}
	};

	const getWeeklyHoursData = async () => {
		try {
			const res = await httpService.api<
				HomePageWeeklyHoursDataResponse[]
			>({
				type: 'getWeeklyHoursData',
				params: {
					date: moment().format('YYYY-MM-DD'),
				},
			});
			setWeeklyData(res);
		} catch (e) {
			console.log(
				`Error while fetching data from API: getWeeklyHoursData`,
				e,
			);
		}
	};

	const accessMatcher = (employeeFormStatus: boolean) => {
		let bothP = false;
		let leftP = false;
		let paycheckP = false;
		let _isSummaryOnly = true;
		employeePrivilegesStatus?.forEach((priv: string) => {
			if (Home_Both_Permissions.includes(priv)) {
				bothP = true;
			}
			if (Home_One_Permissions.includes(priv)) {
				leftP = true;
			}
			if (Home_Paycheck_Permissions.includes(priv)) {
				paycheckP = true;
			}
		});
		if (
			bothP &&
			leftP &&
			employeePrivilegesStatus?.includes(Privileges.ROLE_LOG_WORK_WRITE)
		) {
			_isSummaryOnly = false;
		}
		if (
			moment
				.utc(getState().dateTime.currentDateTime)
				.local()
				.isAfter(
					moment(
						userData?.operative_module?.[101]?.finish_work,
						'YYYY-MM-DD',
					),
				)
		) {
			_isSummaryOnly = true;
		}
		setIsSummaryOnly(_isSummaryOnly);
		setIsPaycheckBlockShown(paycheckP && employeeFormStatus);
	};

	const setShowProjectListHandler = (val: boolean): void => {
		setShowProjectList(val);
	};

	const openDrawer = useCallback(() => {
		navigation.openDrawer();
	}, []);

	const monthlyAttendanceData = useMonthAttendance({
		month: CURRENT_MONTH,
		year: CURRENT_YEAR,
		enabled: isSummaryOnly === false,
	});

	if (!userData || isSummaryOnly === null) {
		return null;
	}
	return (
		<>
			<InAppMessages userRole={Role.EMPLOYEE} />

			<HomePage
				navigation={navigation}
				openDrawer={openDrawer}
				showProjectList={showProjectList}
				setShowProjectList={setShowProjectListHandler}
				isSummaryOnly={isSummaryOnly}
				isPaycheckBlockShown={isPaycheckBlockShown}
				reportDateYear={reportDateYear}
				reportContentData={reportContentData}
				report101warning={report101warning}
				form101Id={form101Id}
				form101Status={form101Status}
				form101StatusCode={form101StatusCode}
				form101LastUpdate={form101LastUpdate}
				monthlyAttendanceData={monthlyAttendanceData}
				monthlyHoursData={monthlyHoursData}
				weeklyData={weeklyData}
			/>
		</>
	);
};

export default HomePageContainer;
